import { shallowEqual, useSelector } from 'react-redux';

import { useProductSetFromCart } from 'src/app/shared/hooks/useProductSetFromCart';
import { useShoppingCart } from 'src/app/shared/hooks/useShoppingCart';
import { getFeaturesByProductId } from 'src/app/utils/getFeaturesByProductId';
import { getProductByProductId } from 'src/app/utils/getProductByProductId';
import type { GetAdProductsForAdPostingQuery } from 'src/graphql/generated';
import type { AppState } from 'src/types/appState';

interface Paywall {
  greyedOutFeatures: Feature[];
  isPaywallVisible: boolean;
}

type Feature = Extract<
  GetAdProductsForAdPostingQuery['adProductsForAdPosting'],
  { __typename?: 'ProductList' }
>['items'][0]['features'][0];

const isSameType = (feature1: Feature, feature2: Feature) =>
  feature1?.type === feature2?.type;

const getFeatureDifferences = (feature1: Feature[], feature2: Feature[]) =>
  feature1
    .filter((f1) => !feature2.find((f2) => isSameType(f1, f2)))
    .filter((f) => f !== null);

export const usePaywall = (): Paywall => {
  const { draftId } = useSelector(
    (state: AppState) => ({
      draftId: state.session.draftId,
    }),
    shallowEqual,
  );
  const products = useProductSetFromCart({ draftId });
  const { cartState, cartFeatures } = useShoppingCart();

  const defaultReturn = {
    greyedOutFeatures: [],
    isPaywallVisible: false,
  };

  if (!products) {
    return defaultReturn;
  }

  const selectedProductId = cartState?.items?.[0]?.product.id;

  if (!selectedProductId) {
    return defaultReturn;
  }

  const selectedProduct = getProductByProductId(products, selectedProductId);

  if (!selectedProduct) {
    return defaultReturn;
  }

  if (selectedProduct.__typename !== 'AdProduct') {
    return defaultReturn;
  }

  const upgradePathsProducts = selectedProduct.upgradePaths.map(
    (upgradePath) => upgradePath.upgradeProduct,
  );

  const upgradePathsProductIds = selectedProduct.upgradePaths.map(
    (upgradePath) => upgradePath.upgradeProduct.id,
  );

  const upgradePathsFeatures = getFeaturesByProductId(
    products,
    upgradePathsProductIds[0],
  );

  if (upgradePathsProducts.length === 0) {
    return defaultReturn;
  }

  if (!upgradePathsFeatures) {
    return defaultReturn;
  }

  return {
    greyedOutFeatures: getFeatureDifferences(
      upgradePathsFeatures,
      cartFeatures,
    ),
    isPaywallVisible: true,
  };
};
